import { useTRPC } from '@/utils/trpc';
import { useValueSetExpand, type UseValueSetExpandResult } from '@liza/ui/hooks/useValueSetExpand';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import type { AddLabOrderSubmission, AsyncSearchProps, PerformingLabOption, TestOption } from './AddLabOrderForm';

const ICD10_VALUE_SET_URL = 'http://hl7.org/fhir/sid/icd-10-cm/vs';
// Pinned literal — `labOrder.create` validates `coding[].system` against `z.literal(ICD10_SYSTEM)`,
// so widening to `string` here breaks the inferred input type at the call site.
const ICD10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10-cm' as const;

type UseLabOrderFormDataOptions = {
  /**
   * Gate the autocomplete/diagnosis queries and reset transient search state when false. Pass the
   * dialog's open state; defaults to `true` for the drawer, which unmounts instead of toggling.
   */
  enabled?: boolean;
  /** Called after an order is created and accepted (not rejected), so the surface can close. */
  onCreated: () => void;
};

export type UseLabOrderFormData = {
  performingLab: AsyncSearchProps<PerformingLabOption>;
  tests: AsyncSearchProps<TestOption>;
  diagnoses: UseValueSetExpandResult;
  isCreating: boolean;
  onPerformingLabChange: (lab: PerformingLabOption | null) => void;
  handleCreate: (values: AddLabOrderSubmission) => Promise<void>;
};

/**
 * Shared data wiring for the lab-order form — the autocomplete queries (performing labs, per-lab
 * test catalog, ICD-10 diagnoses) and the `labOrder.create` mutation — so the modal dialog and the
 * timeline drawer render the same `AddLabOrderForm` without duplicating any of it. Presentation
 * (dialog chrome vs. drawer header) stays with each caller.
 */
export const useLabOrderFormData = (
  medplumPatientId: string,
  { enabled = true, onCreated }: UseLabOrderFormDataOptions,
): UseLabOrderFormData => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [labQuery, setLabQuery] = useState('');
  const [testQuery, setTestQuery] = useState('');
  const [selectedLab, setSelectedLab] = useState<PerformingLabOption | null>(null);

  // Reset transient autocomplete state when disabled (the dialog closing) so re-opening doesn't
  // show stale results. A no-op for the drawer, which unmounts rather than toggling `enabled`.
  useEffect(() => {
    if (!enabled) {
      setLabQuery('');
      setTestQuery('');
      setSelectedLab(null);
    }
  }, [enabled]);

  const debouncedSetLabQuery = useDebouncedCallback(setLabQuery, 250);
  const debouncedSetTestQuery = useDebouncedCallback(setTestQuery, 250);

  const labsQuery = useQuery(trpc.labOrder.searchPerformingLabs.queryOptions({ query: labQuery }, { enabled }));

  const testsQuery = useQuery(
    trpc.labOrder.searchTestCatalog.queryOptions(
      { query: testQuery, labHgId: selectedLab?.hgId ?? '' },
      { enabled: enabled && !!selectedLab },
    ),
  );

  const performingLab: AsyncSearchProps<PerformingLabOption> = {
    items: labsQuery.data?.labs ?? [],
    onQueryChange: debouncedSetLabQuery,
    isFetching: labsQuery.isFetching,
    error: labsQuery.error instanceof Error ? labsQuery.error.message : undefined,
  };

  const tests: AsyncSearchProps<TestOption> = {
    items: (testsQuery.data?.tests ?? []).flatMap((coding) =>
      coding.code ? [{ system: coding.system, code: coding.code, display: coding.display }] : [],
    ),
    onQueryChange: debouncedSetTestQuery,
    isFetching: testsQuery.isFetching,
    error: testsQuery.error instanceof Error ? testsQuery.error.message : undefined,
  };

  const diagnoses = useValueSetExpand({
    queryOptions: (filter) => trpc.valueSet.expand.queryOptions({ url: ICD10_VALUE_SET_URL, filter, count: 20 }),
    enabled,
  });

  const { mutateAsync: createLabOrder, isPending: isCreating } = useMutation(trpc.labOrder.create.mutationOptions());

  const handleCreate = useCallback(
    async (values: AddLabOrderSubmission): Promise<void> => {
      try {
        const result = await createLabOrder({
          medplumPatientId,
          performingLabMedplumId: values.performingLab.medplumId,
          // The picker yields a timezone-less local datetime (`yyyy-MM-dd'T'HH:mm`). Convert it to a
          // UTC instant here, where the browser's timezone is known, so the server stores the
          // clinician's intended moment regardless of where it runs. `new Date(localString)` parses
          // as local time per the ECMAScript spec; `.toISOString()` then normalizes to UTC.
          specimenCollectedDateTime: values.specimenCollectedDateTime
            ? new Date(values.specimenCollectedDateTime).toISOString()
            : undefined,
          selectedTests: values.selectedTests.map((test) => ({
            system: test.system,
            code: test.code,
            display: test.display,
            ...(test.priority && { priority: test.priority }),
            ...(test.notes?.trim() && { notes: test.notes.trim() }),
            ...(test.aoeResponses ? { aoeResponses: test.aoeResponses } : {}),
          })),
          diagnoses: values.diagnoses.map((diagnosis) => ({
            // The diagnosis picker is sourced from the ICD-10-CM ValueSet expansion, so the
            // `system` is guaranteed to be ICD-10-CM regardless of what (if anything) the picker
            // returned. Pin the literal so the tRPC `z.literal(ICD10_SYSTEM)` validator accepts it.
            coding: [
              {
                system: ICD10_SYSTEM,
                code: diagnosis.code,
                ...(diagnosis.display && { display: diagnosis.display }),
              },
            ],
          })),
          orderNotes: values.orderNotes.trim() || undefined,
        });
        if (result.status === 'rejected') {
          // Order persisted in Medplum but Health Gorilla rejected the transmission. Surface HG's
          // reason if it gave us one; keep the surface open so the clinician can correct and retry.
          // We deliberately don't invalidate here — the rejected order shouldn't flash into the
          // timeline/SR tab while the clinician is still reading the toast.
          toast.error(`${t('labOrderRejected')}${result.reason ? `: ${result.reason}` : ''}`);
          return;
        }
        // ServiceRequest list query is keyed by `medplumPatientId`; invalidate so the new order
        // shows up on the timeline and in the SR tab once HG flips status from draft → active.
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.serviceRequest.list.queryKey({ medplumPatientId }),
        });
        toast.success(t('labOrderSubmitted'));
        onCreated();
      } catch (error) {
        toast.error(`${t('labOrderFailed')}: ${error instanceof Error ? error.message : ''}`);
        throw error;
      }
    },
    [createLabOrder, medplumPatientId, onCreated, queryClient, t, trpc.patient.serviceRequest.list],
  );

  return {
    performingLab,
    tests,
    diagnoses,
    isCreating,
    onPerformingLabChange: setSelectedLab,
    handleCreate,
  };
};
